import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Users, Plus, Building2, Crown, Shield, User, MoreHorizontal,
  UserPlus, Ban, CheckCircle, UserCog, Trash2, Mail, KeyRound,
  Clock, X, ChevronRight, AtSign, Activity, ChevronDown, Eye,
} from "lucide-react";

const ROLE_CONFIG = {
  owner:  { icon: Crown,  label: "Owner",  color: "text-amber-600 bg-amber-50 border-amber-200" },
  admin:  { icon: Shield, label: "Admin",  color: "text-blue-600 bg-blue-50 border-blue-200" },
  member: { icon: User,   label: "Member", color: "text-zinc-500 bg-[#F5F5F5] border-[#E5E5E5]" },
};

function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.member;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`text-[10px] font-mono capitalize ${cfg.color}`}>
      <Icon size={9} className="mr-1" /> {cfg.label}
    </Badge>
  );
}

// ═══════════════════════════════════════════
// TEAM ACTIVITY FEED (read-only view on existing activity_logs)
// ═══════════════════════════════════════════
const ORG_EVENT_TYPES = [
  "organization_member_invited",
  "organization_member_joined",
  "organization_member_role_changed",
  "organization_member_disabled",
  "organization_member_enabled",
  "organization_member_removed",
  "sender_access_granted",
  "sender_access_revoked",
];

const ORG_EVENT_ICONS = {
  organization_member_invited: { icon: Mail, color: "text-blue-600", bg: "bg-blue-50" },
  organization_member_joined: { icon: UserPlus, color: "text-emerald-600", bg: "bg-emerald-50" },
  organization_member_role_changed: { icon: Shield, color: "text-violet-600", bg: "bg-violet-50" },
  organization_member_disabled: { icon: Ban, color: "text-red-500", bg: "bg-red-50" },
  organization_member_enabled: { icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
  organization_member_removed: { icon: Trash2, color: "text-red-500", bg: "bg-red-50" },
  sender_access_granted: { icon: KeyRound, color: "text-teal-600", bg: "bg-teal-50" },
  sender_access_revoked: { icon: Eye, color: "text-amber-600", bg: "bg-amber-50" },
};

function formatOrgEvent(log) {
  const m = log.metadata || {};
  switch (log.event_type) {
    case "organization_member_invited":
      return `Invited ${m.invited_email || "someone"} as ${m.role || "member"}`;
    case "organization_member_joined":
      return `${m.email || "Someone"} joined the team`;
    case "organization_member_role_changed":
      return `Role changed: ${m.old_role || "?"} → ${m.new_role || "?"}`;
    case "organization_member_disabled":
      return "Member disabled";
    case "organization_member_enabled":
      return "Member re-enabled";
    case "organization_member_removed":
      return "Member removed";
    case "sender_access_granted":
      return `Sender access granted${m.alias_address ? ` to ${m.alias_address}` : ""}`;
    case "sender_access_revoked":
      return `Sender access revoked${m.alias_address ? ` from ${m.alias_address}` : ""}`;
    default:
      return log.event_type.replace(/_/g, " ");
  }
}

function formatTimeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
}

function TeamActivityFeed({ orgId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);

  const fetchLogs = useCallback(async (append = false, existingCursor = null) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const params = new URLSearchParams({
        entity_type: "organization",
        entity_id: orgId,
        limit: "20",
      });
      if (append && existingCursor) params.set("cursor", existingCursor);
      const { data } = await api.get(`/activity-logs?${params}`);
      const filtered = (data.logs || []).filter((l) => ORG_EVENT_TYPES.includes(l.event_type));
      append ? setLogs((prev) => [...prev, ...filtered]) : setLogs(filtered);
      setHasMore(data.has_more);
      setCursor(data.next_cursor);
    } catch { /* silent */ } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [orgId]);

  useEffect(() => { if (orgId) fetchLogs(false); }, [orgId, fetchLogs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-24">
        <div className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-8" data-testid="team-activity-empty">
        <Activity size={22} className="mx-auto mb-1.5 text-zinc-300" />
        <p className="text-xs text-zinc-500">No team activity yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-0.5" data-testid="team-activity-feed">
      {logs.map((log) => {
        const cfg = ORG_EVENT_ICONS[log.event_type] || { icon: Activity, color: "text-zinc-500", bg: "bg-[#F5F5F5]" };
        const Icon = cfg.icon;
        return (
          <div
            key={log.log_id}
            className="flex items-start gap-2.5 px-1 py-2 rounded-md hover:bg-[#F5F5F5] transition-colors"
            data-testid={`team-event-${log.log_id}`}
          >
            <div className={`w-6 h-6 rounded-full ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
              <Icon size={11} className={cfg.color} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#555555] leading-snug">{formatOrgEvent(log)}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">{formatTimeShort(log.created_at)}</p>
            </div>
          </div>
        );
      })}
      {hasMore && (
        <div className="pt-1 flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => cursor && fetchLogs(true, cursor)}
            disabled={loadingMore}
            className="text-xs text-zinc-500 h-7"
            data-testid="team-activity-load-more"
          >
            {loadingMore ? (
              <div className="w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin mr-1" />
            ) : (
              <ChevronDown size={12} className="mr-1" />
            )}
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// CREATE ORG SECTION
// ═══════════════════════════════════════════
function CreateOrgSection({ onCreated }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/org", { name: name.trim() });
      toast.success("Organization created");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto text-center space-y-6 py-16" data-testid="create-org-section">
      <div className="w-16 h-16 rounded-2xl bg-[#F5F5F5] flex items-center justify-center mx-auto">
        <Building2 size={28} className="text-zinc-500" />
      </div>
      <div>
        <h2 className="text-xl font-semibold text-[#111111] tracking-tight">Create your team</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Set up an organization to invite team members and share alias access.
        </p>
      </div>
      <form onSubmit={handleCreate} className="flex gap-2 max-w-sm mx-auto">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. My Company"
          maxLength={100}
          className="flex-1"
          data-testid="create-org-name-input"
        />
        <Button type="submit" disabled={loading || !name.trim()} data-testid="create-org-submit">
          {loading ? "..." : "Create"}
        </Button>
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════
// INVITE DIALOG
// ═══════════════════════════════════════════
function InviteDialog({ open, onOpenChange, aliases, onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [selectedAliases, setSelectedAliases] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/org/invite", {
        email: email.trim().toLowerCase(),
        role,
        sender_ids: selectedAliases,
      });
      toast.success(`Invitation sent to ${email}`);
      setEmail("");
      setRole("member");
      setSelectedAliases([]);
      onInvited();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleAlias = (aliasId) => {
    setSelectedAliases((prev) =>
      prev.includes(aliasId) ? prev.filter((a) => a !== aliasId) : [...prev, aliasId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={18} /> Invite member
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Email address</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              autoFocus
              data-testid="invite-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invite-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member — access assigned senders only</SelectItem>
                <SelectItem value="admin">Admin — can invite and manage access</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {aliases.length > 0 && (
            <div className="space-y-2">
              <Label>Sender access (aliases)</Label>
              <p className="text-xs text-zinc-500">Select which aliases this member can access</p>
              <div className="max-h-32 overflow-y-auto border border-[#E5E5E5] rounded-md divide-y divide-[#2A2A2A]">
                {aliases.map((a) => (
                  <label
                    key={a.alias_id}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-[#F5F5F5] cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedAliases.includes(a.alias_id)}
                      onChange={() => toggleAlias(a.alias_id)}
                      className="rounded border-zinc-300"
                    />
                    <AtSign size={13} className="text-zinc-500" />
                    <span className="text-[#555555] font-mono text-xs">{a.alias_address || a.alias_id}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={loading || !email.trim()} data-testid="invite-submit-btn">
              {loading ? "Sending..." : "Send invitation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════
// SENDER ACCESS DIALOG
// ═══════════════════════════════════════════
function SenderAccessDialog({ open, onOpenChange, member, aliases, onChanged }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchGrants = useCallback(async () => {
    if (!member?.member_id) return;
    try {
      const { data } = await api.get(`/org/members/${member.member_id}/sender-access`);
      setGrants(data.grants || []);
    } catch { /* */ }
  }, [member?.member_id]);

  useEffect(() => {
    if (open && member?.member_id) fetchGrants();
  }, [open, member?.member_id, fetchGrants]);

  const grantedIds = grants.map((g) => g.alias_id);

  const handleGrant = async (aliasId) => {
    setLoading(true);
    try {
      await api.post(`/org/members/${member.member_id}/sender-access`, { alias_id: aliasId });
      toast.success("Access granted");
      fetchGrants();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (aliasId) => {
    setLoading(true);
    try {
      await api.delete(`/org/members/${member.member_id}/sender-access/${aliasId}`);
      toast.success("Access revoked");
      fetchGrants();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="sender-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={18} /> Sender access for {member?.name || member?.email}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-1 max-h-60 overflow-y-auto" data-testid="sender-access-list">
          {aliases.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-4">No aliases available</p>
          ) : (
            aliases.map((a) => {
              const hasAccess = grantedIds.includes(a.alias_id);
              return (
                <div key={a.alias_id} className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-[#F5F5F5]">
                  <div className="flex items-center gap-2">
                    <AtSign size={13} className="text-zinc-500" />
                    <span className="text-sm font-mono text-[#555555]">{a.alias_address || a.alias_id}</span>
                  </div>
                  <Button
                    size="sm"
                    variant={hasAccess ? "destructive" : "outline"}
                    className="h-7 text-xs"
                    disabled={loading}
                    onClick={() => hasAccess ? handleRevoke(a.alias_id) : handleGrant(a.alias_id)}
                    data-testid={`sender-access-toggle-${a.alias_id}`}
                  >
                    {hasAccess ? "Revoke" : "Grant"}
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════
// MAIN TEAM PAGE
// ═══════════════════════════════════════════
export default function TeamPage() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [accessMember, setAccessMember] = useState(null);
  const [tab, setTab] = useState("members");

  const fetchOrg = useCallback(async () => {
    try {
      const { data } = await api.get("/org");
      if (data.org_id) {
        setOrg(data);
      } else {
        setOrg(null);
      }
    } catch {
      setOrg(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const { data } = await api.get("/org/members");
      setMembers(data.members || []);
    } catch { /* */ }
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const { data } = await api.get("/org/invitations");
      setInvitations(data.invitations || []);
    } catch { /* */ }
  }, []);

  const fetchAliases = useCallback(async () => {
    try {
      const { data } = await api.get("/org/aliases");
      setAliases(data.aliases || []);
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  useEffect(() => {
    if (org) {
      fetchMembers();
      fetchInvitations();
      if (org.my_role === "owner" || org.my_role === "admin") {
        fetchAliases();
      }
    }
  }, [org, fetchMembers, fetchInvitations, fetchAliases]);

  const handleRoleChange = async (memberId, newRole) => {
    try {
      await api.put(`/org/members/${memberId}/role`, { role: newRole });
      toast.success("Role updated");
      fetchMembers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleStatusChange = async (memberId, newStatus) => {
    try {
      await api.put(`/org/members/${memberId}/status`, { status: newStatus });
      toast.success(newStatus === "disabled" ? "Member disabled" : "Member enabled");
      fetchMembers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleRemove = async (memberId) => {
    try {
      await api.delete(`/org/members/${memberId}`);
      toast.success("Member removed");
      fetchMembers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleRevokeInvite = async (invitationId) => {
    try {
      await api.post(`/org/invitations/${invitationId}/revoke`);
      toast.success("Invitation revoked");
      fetchInvitations();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const isManager = org?.my_role === "owner" || org?.my_role === "admin";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!org) {
    return (
      <ScrollArea className="h-full">
        <div className="max-w-2xl mx-auto p-6">
          <CreateOrgSection onCreated={(o) => { setOrg(o); setLoading(false); }} />
        </div>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="max-w-3xl mx-auto p-6 space-y-6 page-enter" data-testid="team-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F5F5F5] flex items-center justify-center">
              <Building2 size={18} className="text-zinc-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[#111111] tracking-tight" data-testid="team-org-name">{org.name}</h2>
              <p className="text-xs text-zinc-500">
                {org.member_count} member{org.member_count !== 1 ? "s" : ""} · Your role: <span className="capitalize font-medium">{org.my_role}</span>
              </p>
            </div>
          </div>
          {isManager && (
            <Button size="sm" onClick={() => setInviteOpen(true)} data-testid="invite-member-btn">
              <UserPlus size={15} className="mr-1.5" /> Invite
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#E5E5E5]" data-testid="team-tabs">
          {[
            { key: "members", label: "Members" },
            { key: "invitations", label: `Invitations${invitations.length > 0 ? ` (${invitations.length})` : ""}` },
            { key: "activity", label: "Activity" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? "border-zinc-950 text-[#111111]"
                  : "border-transparent text-zinc-500 hover:text-[#888888]"
              }`}
              data-testid={`team-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Members tab */}
        {tab === "members" && (
          <div className="border border-[#E5E5E5] rounded-lg overflow-hidden bg-white" data-testid="members-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Member</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Role</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Status</th>
                  {isManager && <th className="w-12"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2A2A2A]">
                {members.map((m, i) => (
                  <tr key={m.member_id || i} className="hover:bg-[#F5F5F5]" data-testid={`member-row-${i}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#111111]">{m.name || "—"}</p>
                      <p className="text-xs font-mono text-zinc-500">{m.email}</p>
                    </td>
                    <td className="px-4 py-3"><RoleBadge role={m.role} /></td>
                    <td className="px-4 py-3">
                      <Badge className={`text-[10px] ${
                        m.status === "active" ? "bg-green-50 text-green-600 border-green-200"
                        : m.status === "disabled" ? "bg-red-50 text-red-600 border-red-200"
                        : "bg-[#F5F5F5] text-zinc-500 border-[#E5E5E5]"
                      }`}>
                        {m.status}
                      </Badge>
                    </td>
                    {isManager && (
                      <td className="px-4 py-3">
                        {m.role !== "owner" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`member-actions-${i}`}>
                                <MoreHorizontal size={15} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {m.member_id && (
                                <DropdownMenuItem
                                  onClick={() => setAccessMember(m)}
                                  data-testid={`member-sender-access-${i}`}
                                >
                                  <KeyRound size={14} className="mr-2" /> Sender access
                                </DropdownMenuItem>
                              )}
                              {org.my_role === "owner" && m.role === "member" && (
                                <DropdownMenuItem
                                  onClick={() => handleRoleChange(m.member_id, "admin")}
                                  data-testid={`member-promote-${i}`}
                                >
                                  <Shield size={14} className="mr-2" /> Promote to Admin
                                </DropdownMenuItem>
                              )}
                              {org.my_role === "owner" && m.role === "admin" && (
                                <DropdownMenuItem
                                  onClick={() => handleRoleChange(m.member_id, "member")}
                                  data-testid={`member-demote-${i}`}
                                >
                                  <User size={14} className="mr-2" /> Demote to Member
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {m.status === "active" ? (
                                <DropdownMenuItem
                                  onClick={() => handleStatusChange(m.member_id, "disabled")}
                                  className="text-amber-600 focus:text-amber-600"
                                  data-testid={`member-disable-${i}`}
                                >
                                  <Ban size={14} className="mr-2" /> Disable
                                </DropdownMenuItem>
                              ) : m.status === "disabled" ? (
                                <DropdownMenuItem
                                  onClick={() => handleStatusChange(m.member_id, "active")}
                                  className="text-green-600 focus:text-green-600"
                                  data-testid={`member-enable-${i}`}
                                >
                                  <CheckCircle size={14} className="mr-2" /> Enable
                                </DropdownMenuItem>
                              ) : null}
                              <DropdownMenuItem
                                onClick={() => handleRemove(m.member_id)}
                                className="text-red-600 focus:text-red-600"
                                data-testid={`member-remove-${i}`}
                              >
                                <Trash2 size={14} className="mr-2" /> Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Invitations tab */}
        {tab === "invitations" && (
          <div data-testid="invitations-section">
            {invitations.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Mail size={28} className="mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No pending invitations</p>
              </div>
            ) : (
              <div className="space-y-2">
                {invitations.map((inv) => (
                  <div
                    key={inv.invitation_id}
                    className="flex items-center justify-between p-3 rounded-lg border border-[#E5E5E5] bg-white"
                    data-testid={`invitation-${inv.invitation_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center">
                        <Mail size={14} className="text-zinc-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#555555]">{inv.email}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <RoleBadge role={inv.role} />
                          <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                            <Clock size={9} />
                            {inv.is_expired ? "Expired" : "Pending"}
                          </span>
                        </div>
                      </div>
                    </div>
                    {isManager && !inv.is_expired && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600 h-7 text-xs"
                        onClick={() => handleRevokeInvite(inv.invitation_id)}
                        data-testid={`revoke-invite-${inv.invitation_id}`}
                      >
                        <X size={13} className="mr-1" /> Revoke
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Activity tab */}
        {tab === "activity" && (
          <div className="border border-[#E5E5E5] rounded-lg bg-white p-4" data-testid="team-activity-section">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={15} className="text-zinc-500" />
              <h3 className="text-sm font-medium text-[#555555]">Recent Team Activity</h3>
            </div>
            <TeamActivityFeed orgId={org.org_id} />
          </div>
        )}
      </div>

      {/* Dialogs */}
      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        aliases={aliases}
        onInvited={() => { fetchInvitations(); fetchMembers(); }}
      />
      <SenderAccessDialog
        open={!!accessMember}
        onOpenChange={(open) => !open && setAccessMember(null)}
        member={accessMember}
        aliases={aliases}
        onChanged={fetchMembers}
      />
    </ScrollArea>
  );
}
